import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, getAuthHeaders } from '@/services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchProfile = async () => {
    try {
      const res = await authApi.getProfile(getAuthHeaders(token));
      setUser(res.data);
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await authApi.login({ email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
  };

  const register = async (name, email, password, phone, userTypes = ['artist']) => {
    // userTypes 现在是数组
    const res = await authApi.register({ name, email, password, phone, user_types: userTypes });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
  };

  // OAuth login handler
  const loginWithOAuth = useCallback(async (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  // 刷新用户信息
  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const res = await authApi.getProfile(getAuthHeaders(token));
        setUser(res.data);
        return res.data;
      } catch (err) {
        console.error('Failed to refresh user:', err);
      }
    }
    return null;
  }, [token]);

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAdmin, refreshUser, loginWithOAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
